/**
 * Opt-out formatting utilities
 * Shared between frontend components and backend XLSX export.
 */

/** Sentinel string returned by formatOptOutSummary when no recipes are opted out. */
export const OPT_OUT_ALL_INCLUDED = 'All included' as const;

/**
 * Build a human-readable summary string from opt-out data.
 * Input: array of { recipeName, optedOut } for each optional recipe on a menu.
 *
 * Returns:
 *  - "" when the array is empty (no optional recipes on the menu)
 *  - OPT_OUT_ALL_INCLUDED when nothing is opted out
 *  - "No Naan, No Rice" listing each opted-out recipe
 */
export function formatOptOutSummary(
  optionalChoices: { recipeName: string; optedOut: boolean }[]
): string {
  if (optionalChoices.length === 0) return '';
  const optedOut = optionalChoices.filter((c) => c.optedOut);
  if (optedOut.length === 0) return OPT_OUT_ALL_INCLUDED;
  return optedOut.map((c) => `No ${c.recipeName}`).join(', ');
}

/**
 * Immutably toggle a menu recipe ID in an opt-out Set.
 * Returns a new Set with the item added or removed.
 *
 * @param set current opt-out IDs
 * @param menuRecipeId the ID to toggle
 * @param included true = recipe included (remove from opt-outs), false = opted out (add)
 */
export function toggleOptOut(
  set: Set<number>,
  menuRecipeId: number,
  included: boolean
): Set<number> {
  const next = new Set(set);
  if (included) {
    next.delete(menuRecipeId);
  } else {
    next.add(menuRecipeId);
  }
  return next;
}
